import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingSmallIcon from "@mui/icons-material/PendingActionsRounded";
import BlockIcon from "@mui/icons-material/Block";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { useNavigate } from "react-router";
import DatePicker, { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import {
  ClientApi,
  type ClientMappingStatus,
  type FetchClientMappingDashboardItem,
  isSuccessfulClientMappingResponse,
  type UpdateClientMappingStatusAction,
} from "../../API/ClientApi";
import ImagePreviewModal from "./ImagePreviewModal";
import ResultModal from "./ResultModal";
import "./ManageB2CClients.css";

const BASE_URL = `${process.env.REACT_APP_MEDIA_URL}/mobycy`;

registerLocale("en-GB", enGB);

interface DateInputProps {
  label: string;
  onClick?: () => void;
}

// Top-level custom input for react-datepicker (must be outside component body).
const DateRangeInput = React.forwardRef<HTMLDivElement, DateInputProps>(
  ({ label, onClick }, ref) => (
    <div ref={ref} onClick={onClick} className="date-dropdown-trigger">
      <span className="date-dropdown-text">{label}</span>
      <KeyboardArrowDownIcon className="date-dropdown-chevron" />
    </div>
  )
);
DateRangeInput.displayName = "DateRangeInput";

function todayMidnight(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatApiDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`; // "YYYY-MM-DD"
}

function formatDisplayDate(d: Date): string {
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`; // "DD-MM-YYYY"
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error.trim().length > 0) return error;
  return fallback;
}

const ManageB2CClients: React.FC = () => {
  const navigate = useNavigate();

  const size = 10;
  const pageRange = 5; // max page pills shown

  // Dates default to today
  const [fromDate, setFromDate] = useState<Date>(todayMidnight());
  const [toDate, setToDate] = useState<Date>(todayMidnight());
  // react-datepicker range UX:
  // keep endDate null until user selects the end date
  const [endChosen, setEndChosen] = useState<boolean>(false);

  // Search: raw input + debounced value (300ms)
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Status filter
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  // Pagination (0-indexed)
  const [page, setPage] = useState(0);

  // Data
  const [loading, setLoading] = useState(false);
  const [clientReportList, setClientReportList] = useState<
    FetchClientMappingDashboardItem[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [fetchFailed, setFetchFailed] = useState(false);

  // Modals
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<FetchClientMappingDashboardItem | null>(null);
  const [editableClientId, setEditableClientId] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [resultOpen, setResultOpen] = useState(false);
  const [resultAction, setResultAction] = useState<"APPROVED" | "REJECTED" | "UPDATED">(
    "APPROVED"
  );
  const resultOpenTimerRef = React.useRef<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"error" | "success">(
    "error"
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const pageCount = Math.ceil(totalCount / size);

  const dateLabel = `From : ${formatDisplayDate(fromDate)}  To: ${formatDisplayDate(
    endChosen ? toDate : fromDate
  )}`;

  const accentGreen = "#20B288";
  const showErrorSnackbar = useCallback((message: string) => {
    setSnackbarSeverity("error");
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (start) {
      const startMidnight = new Date(start);
      startMidnight.setHours(0, 0, 0, 0);
      setFromDate(startMidnight);
      setEndChosen(false);
    }
    if (end) {
      const endMidnight = new Date(end);
      endMidnight.setHours(0, 0, 0, 0);
      setToDate(endMidnight);
      setEndChosen(true);
      setPage(0);
    }
    // Do NOT return early when end is null — allows partial selection
  };

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const fetchClientReport = useCallback(async () => {
    const clientApi = new ClientApi();
    const token = localStorage.getItem("access_token");
    setLoading(true);

    try {
      const effectiveToDate = endChosen ? toDate : fromDate;
      const categoryFilter = selectedCategory ? selectedCategory : undefined;

      const res = await clientApi.fetchClientMappingDashboard(
        token,
        page,
        size,
        formatApiDate(fromDate),
        formatApiDate(effectiveToDate),
        debouncedSearch,
        selectedStatus || undefined,
        categoryFilter
      );
      if (res.code !== 200) {
        throw new Error(res.message || "Failed to fetch client report");
      }
      setClientReportList(res.data);
      setTotalCount(res.count);
      setFetchFailed(false);
    } catch (error: unknown) {
      console.error("Error fetching client report:", error);
      setFetchFailed(true);
      showErrorSnackbar(
        getErrorMessage(error, "Unable to load client report. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  }, [page, size, fromDate, toDate, endChosen, debouncedSearch, selectedStatus, showErrorSnackbar, selectedCategory]);

  useEffect(() => {
    fetchClientReport();
  }, [fetchClientReport]);

  const handleOpenPreview = (row: FetchClientMappingDashboardItem) => {
    setSelectedRow(row);
    setEditableClientId(row.clientId ?? "");
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedRow(null);
    setEditableClientId("");
  };

  const showResultModal = useCallback((nextAction: "APPROVED" | "REJECTED" | "UPDATED") => {
    if (resultOpenTimerRef.current !== null) {
      window.clearTimeout(resultOpenTimerRef.current);
    }

    setResultAction(nextAction);
    setResultOpen(true);
    resultOpenTimerRef.current = null;
  }, []);

  const handleUpdate = useCallback(
    async (action: UpdateClientMappingStatusAction, uiAction: "APPROVED" | "REJECTED") => {
      if (!selectedRow) return;

      const clientApi = new ClientApi();
      const token = localStorage.getItem("access_token");
      setActionLoading(true);

      try {
        const response = await clientApi.updateClientMappingStatus(
          token,
          action,
          selectedRow.id
        );
        if (!isSuccessfulClientMappingResponse(response.code)) {
          throw new Error(response.message || "Failed to update client status");
        }
        setPreviewOpen(false);
        setSnackbarOpen(false);
        showResultModal(uiAction);
        await fetchClientReport();
      } catch (error: unknown) {
        console.error("Error updating client mapping status:", error);
        showErrorSnackbar(
          getErrorMessage(error, "Unable to update status. Please try again.")
        );
      } finally {
        setActionLoading(false);
      }
    },
    [selectedRow, fetchClientReport, showErrorSnackbar, showResultModal]
  );
  const handleReportDownload = useCallback(async () => {
    const clientApi = new ClientApi();
    const token = localStorage.getItem("access_token");

    if (!token) {
      showErrorSnackbar("Token not found. Please login again.");
      return;
    }

    // ✅ No data case
    if (!clientReportList?.length || totalCount === 0) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Data not found");
      setSnackbarOpen(true);
      return;
    }

    setIsDownloading(true);

    try {
      const effectiveToDate = endChosen ? toDate : fromDate;

      const blobData = await clientApi.downloadClientMappingReport(
        token,
        formatApiDate(fromDate),
        formatApiDate(effectiveToDate),
        selectedStatus || undefined,
        selectedCategory || undefined
      );

      const blob = new Blob([blobData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `client-mapping-report-${formatApiDate(fromDate)}-to-${formatApiDate(
        effectiveToDate
      )}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      setSnackbarSeverity("success");
      setSnackbarMessage("Report downloaded successfully");
      setSnackbarOpen(true);
    } catch (error: unknown) {
      console.error("Error downloading client report:", error);
      showErrorSnackbar(
        getErrorMessage(error, "Unable to download report. Please try again.")
      );
    } finally {
      setIsDownloading(false);
    }
  }, [
    fromDate,
    toDate,
    endChosen,
    selectedStatus,
    clientReportList,
    totalCount,
    showErrorSnackbar,
  ]);
  const handleApprove = useCallback(() => {
    void handleUpdate("APPROVE", "APPROVED");
  }, [handleUpdate]);

  const handleReject = useCallback(() => {
    void handleUpdate("REJECT", "REJECTED");
  }, [handleUpdate]);

  const handleUpdateClientId = useCallback(async () => {
    if (!selectedRow) return;

    const clientApi = new ClientApi();
    const token = localStorage.getItem("access_token");
    const nextClientId = editableClientId.trim();

    if (!nextClientId) {
      showErrorSnackbar("Client ID cannot be empty.");
      return;
    }

    setActionLoading(true);

    try {
      const response = await clientApi.updateClientMappingClientId(
        token,
        selectedRow.id,
        nextClientId
      );
      if (!isSuccessfulClientMappingResponse(response.code)) {
        throw new Error(response.message || "Failed to update client id");
      }

      setPreviewOpen(false);
      setSelectedRow(null);
      setEditableClientId("");
      setSnackbarOpen(false);
      showResultModal("UPDATED");
      await fetchClientReport();
    } catch (error: unknown) {
      console.error("Error updating client id:", error);
      showErrorSnackbar(
        getErrorMessage(error, "Unable to update client id. Please try again.")
      );
    } finally {
      setActionLoading(false);
    }
  }, [editableClientId, fetchClientReport, selectedRow, showErrorSnackbar, showResultModal]);

  useEffect(() => {
    return () => {
      if (resultOpenTimerRef.current !== null) {
        window.clearTimeout(resultOpenTimerRef.current);
      }
    };
  }, []);

  const pagesToShow = Math.min(pageCount, pageRange);
  const startPage = Math.max(
    0,
    Math.min(
      page - Math.floor(pageRange / 2),
      pageCount - pagesToShow
    )
  );
  const pages =
    pageCount > 0
      ? Array.from({ length: pagesToShow }, (_, idx) => startPage + idx)
      : [];

  const isFirstPage = page <= 0;
  const isLastPage = pageCount === 0 ? true : page >= pageCount - 1;

  const previewImageUrl =
    selectedRow?.documentId != null
      ? `${BASE_URL}/api/media/file/${selectedRow.documentId}`
      : "";

  const previewLabel = "Image Name";
  const previewStatus: ClientMappingStatus =
    selectedRow?.status ?? "PENDING";

  const idButtonSx = {
    border: `1px solid ${accentGreen}`,
    borderColor: accentGreen,
    color: accentGreen,
    borderRadius: "999px",
    fontWeight: 600,
    fontSize: "13px",
    px: 1.5,
    py: 0.5,
    minWidth: 0,
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "rgba(32, 178, 136, 0.08)",
      color: accentGreen,
      borderColor: accentGreen,
    },
  };

  const statusConfig: any = {
    PENDING: {
      label: "Pending",
      icon: <PendingSmallIcon sx={{ fontSize: 16, mr: 0.5 }} />,
      color: "#F59E0B",
      bgColor: "#FEF3C7",
    },
    APPROVED: {
      label: "Approved",
      icon: <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />,
      color: "#10B981",
      bgColor: "#D1FAE5",
    },
    REJECTED: {
      label: "Rejected",
      icon: <BlockIcon sx={{ fontSize: 16, mr: 0.5 }} />,
      color: "#EF4444",
      bgColor: "#FEE2E2",
    },
    SKIPPED: {
      label: "Skipped",
      icon: <SettingsBackupRestoreIcon sx={{ fontSize: 16, mr: 0.5 }} />,
      color: "#8B5CF6",
      bgColor: "#EDE9FE",
    },
  };

  return (
    <Box className="manage-b2c-clients-page w-full">
      <Toolbar
        disableGutters
        className="manage-b2c-page-header"
        sx={{ minHeight: { xs: 56, sm: 64 } }}
      >
        <Box className="manage-b2c-page-header-inner">
          <Box className="manage-b2c-page-title-row">

            <Typography component="h1" className="manage-b2c-page-title">
              Manage B2C Clients
            </Typography>
          </Box>

          <Box className="manage-b2c-breadcrumb">
            <HomeOutlinedIcon className="manage-b2c-breadcrumb-home" />
            <span className="manage-b2c-breadcrumb-sep">/</span>
            <button
              type="button"
              className="manage-b2c-breadcrumb-link"
              onClick={() => navigate("/managestore")}
            >
              Dashboard
            </button>
            <span className="manage-b2c-breadcrumb-sep">/</span>
            <span className="manage-b2c-breadcrumb-current">
              Manage B2C Clients
            </span>
          </Box>
        </Box>
      </Toolbar>
      <div className="flex mr-3 justify-end mb-3">
        <Button
          onClick={handleReportDownload}
          disabled={isDownloading}
          className="mr-4"
          style={{
            backgroundColor: isDownloading ? "#ace6d5" : "#20B288",
            padding: "5px 18px",
            color: "#fff",
            borderRadius: "40px",
            textTransform: "none",
          }}
        >
          {isDownloading ? "Downloading..." : "Report Download"}
        </Button>
      </div>
      <Box className="manage-b2c-card">
        <Box className="manage-b2c-card-header-row">
          <Typography component="h2" className="manage-b2c-card-title">
            Client Report
          </Typography>
          <div>
            
          </div>
          <Box className="manage-b2c-filters">
            <Select
              value={selectedCategory || ""}
              onChange={(e) => {
                setSelectedCategory(e.target.value || null);
                setPage(0);
              }}
              displayEmpty
              size="small"
              renderValue={(value) => {
                if (!value) return "All Category";
                if (value === "2w") return "2W";
                if (value === "3w") return "3W";
                return String(value).toUpperCase();
              }}
              sx={{
                minWidth: 150,
                borderRadius: "8px",
                "& .MuiOutlinedInput-input": {
                  padding: "8px 12px",
                  fontWeight: 600,
                  fontSize: "13px",
                },
              }}
            >
              <MenuItem value="all">All Category</MenuItem>
              <MenuItem value="2w">2W</MenuItem>
              <MenuItem value="3w">3W</MenuItem>
            </Select>
            {/* Enhanced Status Filter Dropdown */}
            <Select
              value={selectedStatus || ""}
              onChange={(e) => {
                setSelectedStatus(e.target.value || null);
                setPage(0);
              }}
              displayEmpty
              size="small"
              renderValue={(value) => {
                const statusConfig: { [key: string]: { label: string; icon: React.ReactNode; color: string; bgColor: string } } = {
                  "": { label: "All Status", icon: null, color: accentGreen, bgColor: "transparent" },
                  "PENDING": { label: "Pending", icon: <PendingSmallIcon sx={{ fontSize: 16, mr: 0.5 }} />, color: "#F59E0B", bgColor: "#FEF3C7" },
                  "APPROVED": { label: "Approved", icon: <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />, color: "#10B981", bgColor: "#D1FAE5" },
                  "REJECTED": { label: "Rejected", icon: <BlockIcon sx={{ fontSize: 16, mr: 0.5 }} />, color: "#EF4444", bgColor: "#FEE2E2" },
                  "SKIPPED": { label: "Skipped", icon: <SettingsBackupRestoreIcon sx={{ fontSize: 16, mr: 0.5 }} />, color: "#8B5CF6", bgColor: "#EDE9FE" },
                };
                const config = statusConfig[value];
                return (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {config.icon}
                    <span>{config.label}</span>
                  </Box>
                );
              }}
              sx={{
                minWidth: 180,
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F9FAFB",
                  transition: "all 0.3s ease",
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                    borderWidth: "2px",
                  },
                  "&:hover": {
                    backgroundColor: "#F3F4F6",
                    "& fieldset": {
                      borderColor: accentGreen,
                    },
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#FFFFFF",
                    "& fieldset": {
                      borderColor: accentGreen,
                      borderWidth: "2px",
                    },
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px 12px",
                  fontWeight: 600,
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiSvgIcon-root": {
                  color: accentGreen,
                  transition: "transform 0.3s ease",
                },
                "&:hover .MuiSvgIcon-root": {
                  transform: "scale(1.1)",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "8px",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    "& .MuiMenuItem-root": {
                      borderRadius: "6px",
                      margin: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "13px",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(32, 178, 136, 0.08)",
                        paddingLeft: "20px",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "rgba(32, 178, 136, 0.12)",
                        color: accentGreen,
                        "&:hover": {
                          backgroundColor: "rgba(32, 178, 136, 0.16)",
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#9CA3AF", mr: 1.5 }} />
                  All Status
                </Box>
              </MenuItem>
              <MenuItem value="PENDING">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PendingSmallIcon sx={{ fontSize: 18, mr: 1, color: "#F59E0B" }} />
                  Pending
                </Box>
              </MenuItem>
              <MenuItem value="APPROVED">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon sx={{ fontSize: 18, mr: 1, color: "#10B981" }} />
                  Approved
                </Box>
              </MenuItem>
              <MenuItem value="REJECTED">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <BlockIcon sx={{ fontSize: 18, mr: 1, color: "#EF4444" }} />
                  Rejected
                </Box>
              </MenuItem>
              <MenuItem value="SKIPPED">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <SettingsBackupRestoreIcon sx={{ fontSize: 18, mr: 1, color: "#8B5CF6" }} />
                  Skipped
                </Box>
              </MenuItem>
            </Select>

            <DatePicker
              selectsRange
              startDate={fromDate}
              endDate={endChosen ? toDate : null}
              onChange={handleDateChange}
              locale="en-GB"
              dateFormat="dd/MM/yyyy"
              withPortal
              customInput={<DateRangeInput label={dateLabel} />}
            />

            <TextField
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search"
              className="manage-b2c-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

          </Box>
        </Box>

        {/* 2c. Table */}
        <Table
          className="manage-b2c-table"
          sx={{
            width: "100%",
            mt: 3,
            boxShadow: "none",
            "& tbody tr:hover": { backgroundColor: "transparent" },
          }}
        >
          <TableHead>
            <TableRow>
              {[
                "Date",
                "User Name",
                "Category",
                "Client Selected",
                "Client ID",
                "Address",
                "ID/Screenshot",
                "Rewards",
                "Status",
              ]?.map((col) => (
                <TableCell key={col} className="manage-b2c-th">
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  className="manage-b2c-td manage-b2c-td--empty"
                >
                  <CircularProgress sx={{ color: accentGreen }} />
                </TableCell>
              </TableRow>
            ) : !fetchFailed && clientReportList?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  className="manage-b2c-td manage-b2c-td--empty"
                >
                  No data found
                </TableCell>
              </TableRow>
            ) : fetchFailed && clientReportList?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  className="manage-b2c-td manage-b2c-td--empty"
                >
                  &nbsp;
                </TableCell>
              </TableRow>
            ) : (
              clientReportList?.map((row) => {
                const fileUrl =
                  row.documentId != null
                    ? `${BASE_URL}/api/media/file/${row.documentId}`
                    : "";

                return (
                  <TableRow key={row.id}>
                    <TableCell className="manage-b2c-td">
                      {row.createdTime}
                    </TableCell>
                    <TableCell className="manage-b2c-td">
                      {row.userName ?? "—"}
                    </TableCell>
                     <TableCell className="manage-b2c-td">
                      {row.category ?? "—"}
                    </TableCell>
                    <TableCell className="manage-b2c-td">
                      {row.merchantName}
                    </TableCell>
                    <TableCell className="manage-b2c-td">
                      {row.clientId}
                    </TableCell>
                    <TableCell className="manage-b2c-td">
                      {row.address ?? "—"}
                    </TableCell>
                    <TableCell className="manage-b2c-td">
                      {row.documentId == null ? (
                        "—"
                      ) : (
                        <Box className="view-download-wrap">
                          <Button
                            variant="outlined"
                            sx={idButtonSx}
                            startIcon={
                              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            }
                            onClick={() => handleOpenPreview(row)}
                          >
                            View
                          </Button>

                          <a
                            href={fileUrl}
                            download
                            style={{ textDecoration: "none" }}
                          >
                            <Button
                              variant="outlined"
                              sx={idButtonSx}
                              startIcon={
                                <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                              }
                            >
                              Download
                            </Button>
                          </a>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      className="manage-b2c-td manage-b2c-td-reward"
                      sx={{ color: accentGreen }}
                    >
                      ₹ {row.rewardAmount}
                    </TableCell>
                    <TableCell className="manage-b2c-td">
                      {(() => {
                        const statusKey = row.status?.toUpperCase();
                        const status = statusConfig[statusKey];

                        return (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.2,
                              py: 0.4,
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: status?.color || "#6B7280",
                              backgroundColor: status?.bgColor || "#F3F4F6",
                              textTransform: "capitalize",
                            }}
                          >
                            {status?.icon}
                            {status?.label || row.status?.toLowerCase()}
                          </Box>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        <Box className="manage-b2c-pagination">
          <IconButton
            size="small"
            disabled={isFirstPage}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            sx={{
              color: isFirstPage ? undefined : accentGreen,
              "&.Mui-disabled": { opacity: 0.35 },
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {pages?.map((p) => {
              const isActive = p === page;
              return (
                <Box
                  key={p}
                  onClick={() => setPage(p)}
                  className={`pagination-pill ${isActive
                    ? "pagination-pill--active"
                    : "pagination-pill--inactive"
                    }`}
                >
                  {p + 1}
                </Box>
              );
            })}
          </Box>

          <IconButton
            size="small"
            disabled={isLastPage}
            onClick={() =>
              setPage((p) => Math.min(pageCount - 1, p + 1))
            }
            sx={{
              color: isLastPage ? undefined : accentGreen,
              "&.Mui-disabled": { opacity: 0.35 },
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </Box>

      <ImagePreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        label={previewLabel}
        imageUrl={previewImageUrl}
        status={previewStatus}
        onApprove={handleApprove}
        onReject={handleReject}
        clientId={editableClientId}
        onClientIdChange={setEditableClientId}
        onUpdateClientId={handleUpdateClientId}
        actionLoading={actionLoading}
      />

      <ResultModal
        open={resultOpen}
        action={resultAction}
        onClose={() => {
          setResultOpen(false);
          setPreviewOpen(false);
          setSelectedRow(null);
          setEditableClientId("");
        }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageB2CClients;

